/**
 * <size-guide-units> — an in/cm unit toggle for the size guide drawer.
 *
 * The chart data is measurement RANGES ("24-25.5", "21.5-22"), occasionally a
 * single number ("28"), and sometimes a quoted/annotated value. The first
 * version of this only converted cells whose entire text was a plain number,
 * which meant nothing in a real GOAT size chart ever converted and the toggle
 * looked inert.
 *
 * Now every number inside a cell is converted in place, so ranges work and any
 * surrounding punctuation or separator is preserved. The original inch text is
 * captured once per cell, so switching back and forth is lossless and never
 * compounds rounding.
 */

/** Cells that are purely non-numeric (size labels, dashes) are left alone.
 *  Two patterns on purpose: .test() on a /g/ regex advances lastIndex, so reusing
 *  one regex for both detection and replacement makes detection alternate
 *  true/false from cell to cell. */
const NUMBER_PATTERN_GLOBAL = /\d+(?:\.\d+)?/g;
const HAS_NUMBER = /\d/;
const INCHES_TO_CM = 2.54;

class SizeGuideUnits extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('[data-set-unit]');
      if (!button || !this.contains(button)) return;
      this.setUnit(/** @type {HTMLElement} */ (button).dataset.setUnit);
    });
  }

  /** @param {string | undefined} unit - 'in' or 'cm' */
  setUnit(unit) {
    if (unit !== 'in' && unit !== 'cm') return;
    if (this.dataset.unit === unit) return;

    this.dataset.unit = unit;

    this.querySelectorAll('[data-set-unit]').forEach((button) => {
      const active = /** @type {HTMLElement} */ (button).dataset.setUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.#convert(unit);
  }

  /** @param {'in' | 'cm'} unit */
  #convert(unit) {
    // Data cells only — never headers, whose size labels can themselves be
    // numeric ("32", "34") and must not be converted.
    const cells = this.querySelectorAll('.size-guide-units__content td');

    cells.forEach((cell) => {
      const el = /** @type {HTMLElement} */ (cell);

      // Capture the inch original once, so 'in' always restores exactly.
      if (el.dataset.inText === undefined) {
        const raw = (el.textContent || '').trim();
        if (!HAS_NUMBER.test(raw)) return;
        el.dataset.inText = raw;
      }

      const original = el.dataset.inText;
      if (!original) return;

      if (unit === 'in') {
        el.textContent = original;
        return;
      }

      // Convert each number in place, keeping separators ("24-25.5" -> "60.96-64.77").
      // Two decimals, matching live's `(valueInInches * 2.54).toFixed(2)`.
      el.textContent = original.replace(NUMBER_PATTERN_GLOBAL, (match) => {
        const inches = parseFloat(match);
        if (Number.isNaN(inches)) return match;
        return (inches * INCHES_TO_CM).toFixed(2);
      });
    });
  }
}

if (!customElements.get('size-guide-units')) {
  customElements.define('size-guide-units', SizeGuideUnits);
}
